import React, { useId } from 'react';
import { Icon } from '$shared/components/icon';
import Headline from '$shared/components/headline';
import { IHeadline } from '~/lib/data-contract';
import { ModuleContainer } from '../module-container';
import { StyledCarousel } from './style';
import { CarouselItem } from './carousel-item';

import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

export type M250CarouselProps = {
    id?: string;
    headline?: IHeadline;
    carouselItems?: any[];
    autoPlay?: boolean;
    autoPlayInterval?: number;
    numberOfVisibleSlides?: number;
    orientation?: 'horizontal' | 'vertical';
    height?: 'low' | 'normal';
};

export const M250Carousel = ({
                                 id,
                                 headline,
                                 carouselItems,
                                 numberOfVisibleSlides = 1,
                                 autoPlay,
                                 autoPlayInterval,
                                 orientation = 'horizontal',
                                 height = 'normal',
                             }: M250CarouselProps) => {
    if (!carouselItems?.length) {
        return null;
    }

    const generatedId = useId().replace(/:/g, '');
    const navigationClass = `carousel-navigation-${id || generatedId}`;

    return (
        <ModuleContainer id={id}>
            <StyledCarousel orientation={orientation} height={height} numberOfVisibleSlides={numberOfVisibleSlides}>
                {headline && (
                    <div className="carousel-headline">
                        <Headline
                            as={headline.tag}
                            size={headline.style}
                            hideOnMobile={headline.hideOnMobile}
                            noMargin
                        >
                            {headline.text}
                        </Headline>
                    </div>
                )}

                <div className="carousel-wrapper">
                    <Swiper
                        modules={[Navigation, Pagination, Autoplay]}
                        direction={orientation === 'vertical' ? 'vertical' : 'horizontal'}
                        preventClicksPropagation={true}
                        breakpoints={{
                            0: {
                                slidesPerView: orientation === 'vertical' ? numberOfVisibleSlides + 0.1 : 1.1,
                            },
                            768: {
                                slidesPerView:
                                    orientation === 'vertical'
                                        ? numberOfVisibleSlides + 0.15
                                        : numberOfVisibleSlides + 0.2,
                            },
                        }}
                        spaceBetween={16}
                        slidesPerGroup={1}
                        watchOverflow={true}
                        resistanceRatio={0.5}
                        loop={true}
                        rewind={false}
                        centeredSlides={false}
                        navigation={{
                            prevEl: `.${navigationClass} .swiper-button-prev`,
                            nextEl: `.${navigationClass} .swiper-button-next`,
                        }}
                        pagination={false}
                        autoplay={
                            autoPlay
                                ? {
                                    delay: autoPlayInterval || 4000,
                                    disableOnInteraction: false,
                                    pauseOnMouseEnter: true,
                                }
                                : false
                        }
                        className="swiper-carousel"
                    >
                        {carouselItems.map((item, index) => (
                            <SwiperSlide key={index}>
                                <CarouselItem item={item} />
                            </SwiperSlide>
                        ))}
                    </Swiper>
                    <div className={`carousel-navigation ${navigationClass}`}>
                        <div className="swiper-button-prev"
                             onClick={(e) => {
                                 e.stopPropagation();
                                 e.preventDefault();
                             }}
                             onMouseDown={(e) => e.stopPropagation()}>
                            <Icon icon="arrowLeft" size={16} color="blue"/>
                        </div>
                        <div className="swiper-button-next"
                             onClick={(e) => {
                                 e.stopPropagation();
                                 e.preventDefault();
                             }}
                             onMouseDown={(e) => e.stopPropagation()}>
                            <Icon icon="arrowRight" size={16} color="blue"/>
                        </div>
                    </div>
                </div>
            </StyledCarousel>
        </ModuleContainer>
    );
};